import React, {Dispatch, SetStateAction} from "react";
import {
    AllStates,
    asJsonformsErrorTranslate,
    asJsonformsTranslate,
    JSON_FORMS_CONFIG,
    StepForm,
    TranslateFunction
} from '../Forms';

import schema from './thesis.schema.json';

import {JSONSchema7} from "json-schema";
import {JsonForms} from "@jsonforms/react";
import uischema from "./thesis.uischema.json";
import {materialCells, materialRenderers} from "@jsonforms/material-renderers";
import Ajv, {ErrorObject} from "ajv";
import {FinalProject} from "./Thesis";
import {I18nMessage} from "../Representation";
import {getDebug} from "../Data";

export const EMPTY_FINAL_PROJECT: FinalProject =
    {
        degree_of_obligation: undefined,
        scope: undefined,
        status: undefined
    };

const FORM_NS = "thesis_form.";

export function getFinalThesisEligibilityInfo(allStates: AllStates) {
    const warningMessages: I18nMessage[] = [];
    const infoMessages: I18nMessage[] = [];

    const {finalProject: [finalProject, ]} = allStates;
    if (finalProject === undefined) {
        return {warningMessages: ["missing_data"], infoMessages: [], eligibilityPoints: 0};
    }

    let eligibilityPoints = 0;
    if (finalProject.degree_of_obligation === "mandatory" ||
        (finalProject.degree_of_obligation == "optional" &&
            (finalProject.status === "passed" ||
                finalProject.status === "work_in_progress" ||
                finalProject.status === "planned"))) {
        eligibilityPoints = 10;
    }

    if (getDebug())
        infoMessages.push("Eligibility points at most " + eligibilityPoints);

    return {warningMessages: warningMessages, infoMessages: infoMessages, eligibilityPoints: eligibilityPoints};
}

export class FinalProjectForm extends StepForm {

    constructor() {
        super(schema as JSONSchema7);
    }

    getFormNs(): string | undefined {
        return FORM_NS;
    }

    getTitleKey() {
        return "thesis_title";
    };

    getPage([formData, setFormData]: [any, Dispatch<SetStateAction<any>>],  _allStates: AllStates, t: TranslateFunction, lang: string) {
        return (<React.Fragment>
            <JsonForms
                schema={schema}
                uischema={uischema}
                data={formData}
                renderers={materialRenderers}
                cells={materialCells}
                config={JSON_FORMS_CONFIG}
                i18n={{
                    translate: asJsonformsTranslate(t, FORM_NS),
                    locale: lang,
                    translateError: asJsonformsErrorTranslate(t)
                }}
                additionalErrors={this.validateExtra(formData, t)}
                onChange={({data}) => setFormData(data)}
            />
        </React.Fragment>);
    }

    getContextMessageKeys = (data: any, allStates: AllStates): [I18nMessage[], I18nMessage[]] => {
        const {warningMessages: warningMessages, infoMessages: infoMessages} = getFinalThesisEligibilityInfo(allStates);

        return [warningMessages, infoMessages];
    }

    validate(newAjv: () => Ajv, data: any, t: TranslateFunction, allStates: AllStates): ErrorObject[] {
        const errors = super.validate(newAjv, data, t, allStates);

        const finalProject = ((data as unknown) as FinalProject);
        if (finalProject === undefined) {
            return errors;
        }

        errors.push(...this.validateExtra(finalProject, t));

        return errors;
    }

    validateExtra(finalProject: FinalProject, t: TranslateFunction) {
        const errors = [];

        if (finalProject.degree_of_obligation === "mandatory" && finalProject.status === "not_planned") {
            errors.push({
                instancePath: "/status",
                schemaPath: "#/properties/status/forbidden",
                keyword: "forbidden",
                params: {},
                message: "mandatory final thesis cannot have status non_planned"
            });
        }
        if (!finalProject.references && !finalProject.tor && !finalProject.references_later) {
            const msg = t(FORM_NS + "references.label") + ", " + t(FORM_NS + "tor.label") + ", " + t(FORM_NS + "references_later.label");
            errors.push({
                instancePath: "/references",
                schemaPath: "#/properties/references/required",
                keyword: "requiredOneOf",
                params: {
                    missingProperties: msg
                },
                message: "at least one required: 'references', 'tor', 'references_later'"
            });
            errors.push({
                instancePath: "/tor",
                schemaPath: "#/properties/tor/required",
                keyword: "requiredOneOf",
                params: {
                    missingProperties: msg
                },
                message: "at least one required: 'references', 'tor', 'references_later'"
            });
            errors.push({
                instancePath: "/references_later",
                schemaPath: "#/properties/references_later/required",
                keyword: "requiredOneOf",
                params: {
                    missingProperties: msg
                },
                message: "at least one required: 'references', 'tor', 'references_later'"
            });
        }
        return errors;
    }

    filterValidationErrors(data: any, errors: ErrorObject[] | null | undefined) {
        if (errors === null || errors === undefined) {
            return errors;
        }

        const finalProject = ((data as unknown) as FinalProject);
        if (finalProject === undefined) {
            return errors;
        }

        return errors.filter(() => {
            // if none is selected, the degree_of_obligation is obviously filled in correctly.
            // Since all other fields are hidden in this case, we can drop all these errors.
            return finalProject.degree_of_obligation !== "none";
        });
    }

    getExplanationMarkdownKey() {
        return "thesis.explanation.markdown";
    }

    getHelpUrl(): string {
        return "faq.html#thesis";
    }

}